/**
 * Sprint 6B-12: Standardized OfferCard Component
 * 
 * A reusable component for displaying offer information across the application
 * with role-aware field visibility.
 * 
 * Roles and visibility:
 * - patient: Sees clinic info, treatment, price, inclusions, validity, but NOT sales rep info
 * - sales_rep: Sees full offer details including patient info, can see their own offers
 * - clinic_admin: Sees all offer details, including sales rep who created it
 * - doctor: Sees treatment/clinical details, price, patient info
 * - treatx_admin: Sees everything including internal fields
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Eye, MapPin, Star, Clock, Hotel, DollarSign, 
  User, Calendar, Check, AlertCircle, MessageCircle,
  FileText, Package, Plane, CreditCard, CheckCircle
} from 'lucide-react';
import { isV3Enabled } from '../utils/featureFlags';
import { currencySymbol } from '../utils/currencyPolicy';
import i18n from '../i18n/i18n';
import { formatDateLocale } from '../utils/dateFormat'; // OFFERS-UX-FINAL-CLOSURE — locale-aware date presentation
import OfferCommercialFacts from './OfferCommercialFacts';

// Status configuration with colors and labels
const OFFER_STATUS_CONFIG = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', label: 'Draft', icon: FileText },
  PENDING_APPROVAL: { color: 'bg-amber-100 text-amber-700', label: 'Pending Approval', icon: Clock },
  APPROVED: { color: 'bg-blue-100 text-blue-700', label: 'Approved', icon: Check },
  SENT: { color: 'bg-indigo-100 text-indigo-700', label: 'Sent', icon: FileText },
  PENDING: { color: 'bg-indigo-100 text-indigo-700', label: 'Pending', icon: Clock },
  ACCEPTED: { color: 'bg-green-100 text-green-700', label: 'Accepted', icon: Check },
  REJECTED: { color: 'bg-red-100 text-red-700', label: 'Rejected', icon: AlertCircle },
  EXPIRED: { color: 'bg-slate-100 text-slate-700', label: 'Expired', icon: Clock }
};

// Role-based visibility rules
const FIELD_VISIBILITY = {
  // Fields visible to all roles
  common: ['treatment', 'totalPrice', 'status', 'validUntil', 'includedItems'],
  
  // Patient-specific: NO sales rep info, sees clinic details
  patient: ['clinicName', 'clinicLocation', 'clinicRating', 'hotelNights', 'hotelType', 'airportTransfer', 'proposedDates'],
  
  // Sales Rep: sees patient info, their own offers
  sales_rep: ['patientName', 'patientEmail', 'patientCountry', 'hotelNights', 'hotelType', 'airportTransfer', 'proposedDates', 'notes', 'createdAt'],
  
  // Clinic Admin: sees everything including who created it
  clinic_admin: ['patientName', 'patientEmail', 'patientCountry', 'hotelNights', 'hotelType', 'airportTransfer', 'proposedDates', 'notes', 'createdBy', 'createdByName', 'createdAt', 'basePrice', 'deposit'],
  
  // Doctor: sees clinical + patient details
  doctor: ['patientName', 'patientEmail', 'hotelNights', 'proposedDates', 'notes'],
  
  // TreatX Admin: sees everything
  treatx_admin: ['patientName', 'patientEmail', 'patientCountry', 'clinicName', 'clinicId', 'hotelNights', 'hotelType', 'airportTransfer', 'proposedDates', 'notes', 'createdBy', 'createdByName', 'createdAt', 'basePrice', 'deposit', 'version', 'leadId']
};

// Check if a field is visible for a given role
const ROLE_ALIASES = { provider_admin: 'clinic_admin', provider_sales_rep: 'sales_rep' };
const isFieldVisible = (field, role) => {
  if (FIELD_VISIBILITY.common.includes(field)) return true;
  const r = FIELD_VISIBILITY[role] ? role : ROLE_ALIASES[role];
  return FIELD_VISIBILITY[r]?.includes(field) || false;
};
const sym = (offer) => currencySymbol(offer?.currency || 'EUR');

// Compact card variant (for lists/grids)
const CompactOfferCard = ({ offer, role, onViewDetails, onAction, actionLabel }) => {
  // LIFECYCLE-V3.2 — unknown status renders neutral + observable, never 'Pending'
  const statusConfig = OFFER_STATUS_CONFIG[offer.status] || { color: 'bg-slate-200 text-slate-700', label: String(offer.status || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), unknown: true };
  const StatusIcon = statusConfig.icon;
  const isExpired = offer.validUntil && new Date(offer.validUntil) < new Date();
  
  return (
    <Card 
      className={`overflow-hidden transition-shadow hover:shadow-lg ${isExpired ? 'opacity-60' : ''}`}
      data-testid={`offer-card-${offer.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            {/* Patient view: show clinic info */}
            {role === 'patient' && isFieldVisible('clinicName', role) && (
              <>
                <CardTitle className="text-base truncate">{offer.clinicName}</CardTitle>
                {isFieldVisible('clinicLocation', role) && offer.clinicLocation && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{offer.clinicLocation}</span>
                  </p>
                )}
              </>
            )}
            
            {/* Clinic staff view: show patient info */}
            {role !== 'patient' && isFieldVisible('patientName', role) && (
              <>
                <CardTitle className="text-base truncate">{offer.patientName}</CardTitle>
                {isFieldVisible('patientEmail', role) && offer.patientEmail && (
                  <p className="text-xs text-gray-500 truncate">{offer.patientEmail}</p>
                )}
              </>
            )}
          </div>
          
          {/* Rating for patient view */}
          {role === 'patient' && isFieldVisible('clinicRating', role) && offer.clinicRating && (
            <div className="flex items-center flex-shrink-0 ml-2">
              <Star className="h-4 w-4 text-yellow-500 fill-current" />
              <span className="text-sm ml-1">{offer.clinicRating}</span>
            </div>
          )}
          
          {/* Status badge for clinic staff */}
          {role !== 'patient' && (
            <Badge className={`${statusConfig.color} flex-shrink-0 ml-2`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
          )}
          {/* V3: Selected indicator */}
          {isV3Enabled() && offer.is_selected && (
            <Badge className="bg-green-100 text-green-700 flex-shrink-0 ml-1" data-testid={`v3-selected-badge-${offer.id}`}>
              <CheckCircle className="h-3 w-3 mr-1" />
              Selected
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {/* Treatment & Price */}
        <div className="flex justify-between items-center mb-3">
          <Badge variant="outline" className="text-xs truncate max-w-[60%]">
            {offer.treatment}
          </Badge>
          <span className="text-xl font-bold text-green-600 flex-shrink-0">
            {sym(offer)}{(offer.totalPrice || 0).toLocaleString()}
          </span>
        </div>
        
        {/* Inclusions row */}
        <div className="flex flex-wrap gap-2 mb-3">
          {isFieldVisible('hotelNights', role) && offer.hotelNights > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              <Hotel className="h-3 w-3" />
              <span>{offer.hotelNights}N {offer.hotelType || ''}</span>
            </div>
          )}
          {isFieldVisible('airportTransfer', role) && offer.airportTransfer && (
            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              <Plane className="h-3 w-3" />
              <span>Transfer</span>
            </div>
          )}
        </div>
        
        {/* Validity */}
        {isFieldVisible('validUntil', role) && offer.validUntil && (
          <div className={`flex items-center gap-2 text-xs mb-3 ${isExpired ? 'text-red-500' : 'text-gray-500'}`}>
            <Clock className="h-3 w-3" />
            <span>
              {isExpired ? 'Expired' : 'Valid until'} {formatDateLocale(offer.validUntil, i18n.language)}
            </span>
          </div>
        )}

        {/* V3: Deposit required indicator */}
        {isV3Enabled() && (offer.deposit_required || offer.deposit_amount > 0) && !offer.is_selected && (
          <div className="flex items-center gap-2 text-xs mb-3 text-amber-600" data-testid={`v3-deposit-indicator-${offer.id}`}>
            <CreditCard className="h-3 w-3" />
            <span>Deposit: {offer.currency || 'EUR'} {(offer.deposit_amount || offer.deposit || 0).toLocaleString()}</span>
          </div>
        )}
        
        {/* Sales Rep info for clinic admin */}
        {isFieldVisible('createdByName', role) && offer.createdByName && (
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
            <User className="h-3 w-3" />
            <span>By {offer.createdByName}</span>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2">
          {onViewDetails && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => onViewDetails(offer)}
              data-testid={`offer-view-${offer.id}`}
            >
              <Eye className="h-3 w-3 mr-1" />
              Details
            </Button>
          )}
          {onAction && !isExpired && (
            <Button
              size="sm"
              onClick={() => onAction(offer)}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid={`offer-action-${offer.id}`}
            >
              {actionLabel || 'Select'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Detailed card variant (for modals/detail views)
const DetailedOfferCard = ({ offer, role, onClose, onAction, actionLabel }) => {
  // LIFECYCLE-V3.2 — unknown status renders neutral + observable, never 'Pending'
  const statusConfig = OFFER_STATUS_CONFIG[offer.status] || { color: 'bg-slate-200 text-slate-700', label: String(offer.status || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), unknown: true };
  const isExpired = offer.validUntil && new Date(offer.validUntil) < new Date();
  
  return (
    <div className="space-y-6" data-testid={`offer-detail-${offer.id}`}>
      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          {/* Patient view: Clinic info */}
          {role === 'patient' && isFieldVisible('clinicName', role) && (
            <div className="mb-2">
              <h3 className="text-xl font-bold">{offer.clinicName}</h3>
              {isFieldVisible('clinicLocation', role) && offer.clinicLocation && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {offer.clinicLocation}
                </p>
              )}
            </div>
          )}
          
          {/* Staff view: Patient info */}
          {role !== 'patient' && isFieldVisible('patientName', role) && (
            <div className="mb-2">
              <h3 className="text-xl font-bold">{offer.patientName}</h3>
              {isFieldVisible('patientEmail', role) && (
                <p className="text-sm text-gray-500">{offer.patientEmail}</p>
              )}
              {isFieldVisible('patientCountry', role) && offer.patientCountry && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {offer.patientCountry}
                </p>
              )}
            </div>
          )}
          
          <Badge className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </div>
        
        <div className="text-right">
          <p className="text-3xl font-bold text-green-600">
            {sym(offer)}{(offer.totalPrice || 0).toLocaleString()}
          </p>
          {isFieldVisible('basePrice', role) && offer.basePrice && (
            <p className="text-sm text-gray-500" data-testid="offer-card-base-breakdown">
              Base: {sym(offer)}{offer.basePrice.toLocaleString()}{(offer.additionalServicesTotal || 0) > 0 ? ` + ${sym(offer)}${offer.additionalServicesTotal.toLocaleString()}` : ''}
            </p>
          )}
        </div>
      </div>
      
      {/* OFFER-LIFECYCLE-PRODUCTION-TRUTH-CLOSURE — staff readers consume the
          SAME canonical commercial-facts projection as the Sales Rep view so
          Coordinator detail == Sales Rep view == Patient view (facts, not controls). */}
      {role !== 'patient' ? (
        <OfferCommercialFacts offer={offer} requestedTreatment={offer.requested_treatment_display_name || offer.requested_treatment} />
      ) : (
      <>
      {/* Treatment Section */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Package className="h-5 w-5 text-blue-600" />
          <h4 className="font-semibold">Treatment</h4>
        </div>
        <p className="text-lg font-medium">{offer.treatment}</p>
        
        {/* Included Items */}
        {isFieldVisible('includedItems', role) && offer.includedItems?.length > 0 && (
          <div className="mt-3">
            <p className="text-sm text-gray-600 mb-2">Included:</p>
            <ul className="grid grid-cols-2 gap-1">
              {offer.includedItems.map((item, i) => (
                <li key={i} className="flex items-center gap-1 text-sm">
                  <Check className="h-3 w-3 text-green-500" />
                  {typeof item === 'string' ? item : item.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Travel & Accommodation */}
      {(isFieldVisible('hotelNights', role) && offer.hotelNights > 0) || 
       (isFieldVisible('airportTransfer', role) && offer.airportTransfer) ? (
        <div className="grid grid-cols-2 gap-4">
          {isFieldVisible('hotelNights', role) && offer.hotelNights > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Hotel className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium">Accommodation</span>
              </div>
              <p className="text-sm">{offer.hotelNights} nights - {offer.hotelType || 'Standard'}</p>
            </div>
          )}
          {isFieldVisible('airportTransfer', role) && offer.airportTransfer && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Plane className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium">Transfer</span>
              </div>
              <p className="text-sm">Airport transfer included</p>
            </div>
          )}
        </div>
      ) : null}
      
      {/* Payment Info */}
      {isFieldVisible('deposit', role) && offer.deposit && (
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold">Payment</h4>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Deposit Required</p>
              <p className="font-semibold">{sym(offer)}{offer.deposit.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Balance Due</p>
              <p className="font-semibold" data-testid="offer-card-balance">{offer.balance != null ? `${sym(offer)}${offer.balance.toLocaleString()}` : '—'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Validity & Dates */}
      <div className="grid grid-cols-2 gap-4">
        {isFieldVisible('validUntil', role) && offer.validUntil && (
          <div>
            <p className="text-sm text-gray-500">Valid Until</p>
            <p className={`font-medium ${isExpired ? 'text-red-500' : ''}`}>
              {formatDateLocale(offer.validUntil, i18n.language)}
              {isExpired && ' (Expired)'}
            </p>
          </div>
        )}
        {isFieldVisible('proposedDates', role) && offer.proposedDates && (
          <div>
            <p className="text-sm text-gray-500">Proposed Dates</p>
            <p className="font-medium">{offer.proposedDates}</p>
          </div>
        )}
      </div>
      
      {/* Notes - Staff only */}
      {isFieldVisible('notes', role) && offer.notes && (
        <div>
          <p className="text-sm text-gray-500 mb-1">Notes</p>
          <p className="text-sm bg-gray-50 p-3 rounded">{offer.notes}</p>
        </div>
      )}
      
      </>
      )}

      {/* Meta info for admin */}
      {isFieldVisible('createdByName', role) && (offer.createdByName || offer.createdAt) && (
        <div className="text-sm text-gray-500 border-t pt-3">
          {offer.createdByName && <span>Created by {offer.createdByName}</span>}
          {offer.createdByName && offer.createdAt && <span> • </span>}
          {offer.createdAt && <span>{formatDateLocale(offer.createdAt, i18n.language)}</span>}
          {isFieldVisible('version', role) && offer.version && (
            <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">v{offer.version}</span>
          )}
        </div>
      )}
      
      {/* Actions */}
      {(onClose || onAction) && (
        <div className="flex gap-3 pt-2">
          {onClose && (
            <Button variant="outline" onClick={onClose} className="flex-1">
              Close
            </Button>
          )}
          {onAction && !isExpired && (
            <Button 
              onClick={() => onAction(offer)} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid={`offer-detail-action-${offer.id}`}
            >
              {actionLabel || 'Select Offer'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Table row variant (for admin lists)
const OfferTableRow = ({ offer, role, salesReps, onViewDetails }) => {
  // LIFECYCLE-V3.2 — unknown status renders neutral + observable, never 'Pending'
  const statusConfig = OFFER_STATUS_CONFIG[offer.status] || { color: 'bg-slate-200 text-slate-700', label: String(offer.status || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), unknown: true };
  const rep = salesReps?.find(r => r.id === offer.createdBy);
  
  return (
    <tr className="border-b hover:bg-slate-50" data-testid={`offer-row-${offer.id}`}>
      <td className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium">{offer.patientName}</p>
          {/* BATCH B1 — version/earlier-version marker from canonical is_latest + version_number */}
          {offer.is_latest === false ? (
            <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded" data-testid={`offer-version-earlier-${offer.id}`}>
              {i18n.t('offersOverview.earlierVersion', 'Earlier v{{n}}').replace('{{n}}', String(offer.version || ''))}
            </span>
          ) : (offer.version && offer.version > 1) ? (
            <span className="text-[10px] uppercase tracking-wide bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded" data-testid={`offer-version-latest-${offer.id}`}>
              v{offer.version}
            </span>
          ) : null}
        </div>
        {isFieldVisible('patientEmail', role) && (
          <p className="text-xs text-slate-500">{offer.patientEmail}</p>
        )}
      </td>
      <td className="p-3 text-sm">{offer.treatment}</td>
      {isFieldVisible('createdByName', role) && (
        <td className="p-3">
          <div className="flex items-center gap-2">
            {rep?.avatar && <img src={rep.avatar} alt="" className="w-6 h-6 rounded-full" />}
            <span className="text-sm">{rep?.name || offer.createdByName || i18n.t('provider.messaging.assignedRepresentativeFallback')}</span>
          </div>
        </td>
      )}
      <td className="p-3 text-right font-semibold">
        {sym(offer)}{(offer.totalPrice || 0).toLocaleString()}
      </td>
      <td className="p-3 text-center">
        <Badge className={statusConfig.color}>
          {statusConfig.label}
        </Badge>
      </td>
      <td className="p-3 text-center text-sm text-slate-500">
        {offer.createdAt ? formatDateLocale(offer.createdAt, i18n.language) : '-'}
      </td>
      {onViewDetails && (
        <td className="p-3 text-center">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onViewDetails(offer)}
            data-testid={`offer-row-view-${offer.id}`}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        </td>
      )}
    </tr>
  );
};

// Main export with all variants
export const OfferCard = {
  Compact: CompactOfferCard,
  Detailed: DetailedOfferCard,
  TableRow: OfferTableRow,
  // Utility exports
  STATUS_CONFIG: OFFER_STATUS_CONFIG,
  isFieldVisible,
  FIELD_VISIBILITY
};

export default OfferCard;
